import { Box, Breadcrumbs, type BreadcrumbsProps, IconButton, Stack, Tab, Tabs, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useEditorActiveNode } from '../Editor';
import { getNodeById } from '../utils/utils';

type Props = {
  startIdx?: number;
  breadcrumbProps?: BreadcrumbsProps;
  showAsTabs?: boolean;
  immediateSelection?: boolean;
};

const EditorTreeBreadCrumbs = ({ startIdx = 0, showAsTabs = false, immediateSelection = false, ...props }: Props) => {
  const { activeNodeRoot, activeNode, setActiveNode } = useEditorActiveNode();
  const [tabValue, setTabValue] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    setHasInitialized(false);
  }, [activeNodeRoot?.id]);

  const nodes = useMemo(() => {
    if (!activeNode?.nodePath) return [];

    const tokens = activeNode.nodePath.split(',');
    return tokens
      .map((token) => {
        if (token === activeNodeRoot?.id) return activeNodeRoot;
        if (!activeNodeRoot?.childrenNodes?.size) return null;
        return getNodeById(token, activeNodeRoot.childrenNodes);
      })
      .filter((node): node is NonNullable<typeof node> => node !== null)
      .slice(startIdx);
  }, [activeNode, activeNodeRoot, startIdx]);

  const tabNodes = useMemo(() => {
    if (!showAsTabs || !activeNodeRoot) return [];

    const allNodes: NonNullable<typeof activeNodeRoot>[] = [];
    if (activeNodeRoot) {
      allNodes.push(activeNodeRoot);
    }

    if (activeNodeRoot?.childrenNodes?.size) {
      const childNodes = Array.from(activeNodeRoot.childrenNodes.values())
        .filter((node): node is NonNullable<typeof node> => Boolean(node?.folder?.title))
        .sort((a, b) => (a.folder?.title || '').localeCompare(b.folder?.title || ''));

      for (let i = 0; i < Math.min(2, childNodes.length); i++) {
        allNodes.push(childNodes[i]);
      }
    }

    return allNodes;
  }, [activeNodeRoot, showAsTabs]);

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      if (!tabNodes.length) return;

      setTabValue(newValue);
      const selectedNode = tabNodes[newValue];
      if (selectedNode?.id) {
        setActiveNode(selectedNode);
      }
    },
    [tabNodes, setActiveNode]
  );

  useEffect(() => {
    if (tabNodes.length > 0 && activeNode?.id) {
      let activeIndex = tabNodes.findIndex((node) => node?.id === activeNode?.id);

      if (activeIndex < 0 && activeNode?.nodePath) {
        for (let i = 0; i < tabNodes.length; i++) {
          if (activeNode.nodePath?.includes(tabNodes[i].id)) {
            activeIndex = i;
            break;
          }
        }
      }

      if (activeIndex >= 0) {
        setTabValue(activeIndex);
      }
    }
  }, [tabNodes, activeNode]);

  useEffect(() => {
    if (immediateSelection && tabNodes.length > 0 && !hasInitialized && activeNodeRoot) {
      const rootNode = tabNodes[0];
      if (rootNode?.id) {
        setActiveNode(rootNode);
        setHasInitialized(true);
      }
    }
  }, [immediateSelection, tabNodes, hasInitialized, setActiveNode, activeNodeRoot]);

  if (!activeNode) return null;

  const renderTab = (node: NonNullable<typeof activeNodeRoot>) => {
    if (!node || !node.id) return null;

    return (
      <Tab
        key={node.id}
        icon={React.isValidElement(node?.folder?.icon) ? node?.folder?.icon : undefined}
        iconPosition="start"
        label={
          <Typography
            variant="caption"
            sx={{
              maxWidth: '15ch',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {node?.folder?.title || ''}
          </Typography>
        }
        sx={{
          flex: '1 1 0px',
          minWidth: 0,
        }}
      />
    );
  };

  const renderBreadcrumb = (node: NonNullable<typeof activeNodeRoot>) => {
    if (!node || !node.id) return null;

    return (
      <Stack
        key={node.id}
        direction="row"
        alignItems="center"
        spacing={0}
        p={0}
        onClick={() => setActiveNode(node)}
        sx={{ cursor: 'pointer' }}
      >
        <IconButton size="small" disableRipple component="span" hidden={!node?.folder?.icon}>
          {node?.folder?.icon}
        </IconButton>
        <Typography
          variant="caption"
          sx={{
            maxWidth: '15ch',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {node?.folder?.title || ''}
        </Typography>
      </Stack>
    );
  };

  if (showAsTabs && tabNodes.length > 0) {
    return (
      <Box
        className="editor-tabs-container"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          width: '100%',
          overflow: 'hidden',
        }}
        id="panel-tabs"
        data-panel=""
        data-panel-id="panel-tabs"
        data-panel-size="15.0"
        style={{ flex: '15 1 0px', overflow: 'hidden' }}
      >
        <Tabs
          value={Math.min(tabValue, tabNodes.length - 1)}
          onChange={handleTabChange}
          variant="fullWidth"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-scroller': {
              overflow: 'hidden',
              marginBottom: 0,
            },
            '& .MuiTabs-indicator': {
              display: 'block',
            },
            '& .MuiTabs-flexContainer': {
              display: 'flex',
            },
            '& .MuiTab-fullWidth': {
              flex: '1 1 0px',
            },
          }}
        >
          {tabNodes.filter(Boolean).map(renderTab)}
        </Tabs>
      </Box>
    );
  }

  return <Breadcrumbs {...props.breadcrumbProps}>{nodes.filter(Boolean).map(renderBreadcrumb)}</Breadcrumbs>;
};

export default EditorTreeBreadCrumbs;
